const buildBaseCandidates = () => {
  const candidates = [
    process.env.REACT_APP_RESOURCE_ENDPOINT,
    process.env.REACT_APP_RESOURCE_ENDPOINT,
  ].filter(Boolean);

  if (typeof window !== "undefined" && window.location?.origin) {
    candidates.push(window.location.origin);
  }

  const publicUrl = process.env.PUBLIC_URL;
  if (publicUrl && publicUrl !== "/") {
    candidates.push(publicUrl);
  }

  const apiBases = [
    process.env.REACT_APP_UAT_API_ENDPOINT,
    process.env.REACT_APP_API_ENDPOINT,
  ].filter(Boolean);
  apiBases.forEach((apiBase) => {
    const normalized = apiBase.replace(/\/api\/?$/, "/");
    candidates.push(normalized);
  });

  return Array.from(new Set(candidates));
};

const buildUrl = (base, path) =>
  `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

const tryFetchJson = async (url) => {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error("Response is not JSON");
  }
  return response.json();
};

const _fetchCategoriesService = async (setCategoriesList, setIsLoading) => {
  try {
    setIsLoading(true);
    const timestamp = new Date().getTime();
    const candidates = buildBaseCandidates();
    let data = null;

    for (const base of candidates) {
      const url = buildUrl(base, `Category.json?t=${timestamp}`);
      try {
        data = await tryFetchJson(url);
        break;
      } catch (error) {
        console.warn("Category.json fetch failed:", url, error.message);
      }
    }

    if (!data) {
      throw new Error("No category source returned JSON");
    }
    const businessName = process.env.REACT_APP_BUSINESS_NAME;
    const businessCategories =
      businessName && Array.isArray(data[businessName]) ? data[businessName] : null;
    setCategoriesList(businessCategories || data.Categories || []); // Business-specific or fallback
  } catch (error) {
    console.error("Error fetching categories:", error);
  } finally {
    setIsLoading(false);
  }
};

const _fetchMarketingCategoriesService = async (setCategoriesList, setIsLoading) => {
  try {
    setIsLoading(true);
    const timestamp = new Date().getTime();
    const candidates = buildBaseCandidates();
    let data = null;

    for (const base of candidates) {
      const url = buildUrl(base, `MarketingCategory.json?t=${timestamp}`);
      try {
        data = await tryFetchJson(url);
        break;
      } catch (error) {
        console.warn("MarketingCategory.json fetch failed:", url, error.message);
      }
    }

    if (!data) {
      throw new Error("No marketing category source returned JSON");
    }

    const businessName = process.env.REACT_APP_BUSINESS_NAME;
    const normalizeName = (value) =>
      String(value || "").toLowerCase().replace(/\s+/g, "");
    let marketingCategoryList = [];
    if (Array.isArray(data)) {
      let filtered = data;
      if (businessName) {
        const normalizedBusiness = normalizeName(businessName);
        const lastMatchIndex = data
          .map((item) => normalizeName(item?.businessName))
          .lastIndexOf(normalizedBusiness);
        if (lastMatchIndex >= 0) {
          let startIndex = lastMatchIndex;
          while (
            startIndex > 0 &&
            normalizeName(data[startIndex - 1]?.businessName) === normalizedBusiness
          ) {
            startIndex -= 1;
          }
          filtered = data.slice(startIndex, lastMatchIndex + 1);
        }
      }
      filtered = filtered.filter((item) => item?.isActive === true);
      if (businessName) {
        filtered = filtered.filter(
          (item) => normalizeName(item?.businessName) === normalizeName(businessName)
        );
      }
      marketingCategoryList = filtered
        .map((item) => ({
          title: item?.title || item?.categoryKey || "",
          key: item?.categoryKey || item?.title || "",
        }))
        .filter((item) => item.title || item.key);
    } else if (Array.isArray(data?.Categories)) {
      marketingCategoryList = data.Categories.map((item) => ({
        title: item,
        key: item,
      }));
    }
    setCategoriesList(marketingCategoryList);
  } catch (error) {
    console.error("Error fetching marketing categories:", error);
  } finally {
    setIsLoading(false);
  }
};

const _fetchColorService = async (setCategoriesList, setIsLoading) => {
  try {
    setIsLoading(true);

    // Append a cache-busting query parameter to the URL
    const timestamp = new Date().getTime();
    const url = `${process.env.REACT_APP_RESOURCE_ENDPOINT}/Color.json?t=${timestamp}`;

    const response = await fetch(url);
    const data = await response.json();
    setCategoriesList(data.Categories || []); // Assuming response format { Categories: [...] }
  } catch (error) { 
    console.error('Error fetching categories:', error);
  } finally {
    setIsLoading(false);
  }
};
export { _fetchCategoriesService, _fetchMarketingCategoriesService, _fetchColorService };
